"use client";

import { useAppDispatch } from "@/lib/redux/hooks";
import { ApiParametersType } from "@/types/network";
import { PropsWithChildren, useEffect } from "react";
import { useParams } from "next/navigation";
import { setCurrentEventId, setCurrentWorkspaceIdOrSlug } from "@features/application/app-slice";

/**
 * UrlParamsToStoreProvider is a React component that synchronizes URL parameters
 * with the Redux store state. It acts as a bridge between Next.js routing and the
 * application's global state management.
 *
 * This provider extracts workspace and event identifiers from the current URL
 * and automatically updates the Redux store with these values, ensuring that
 * the application's state remains consistent with the current route.
 *
 * @param {PropsWithChildren} props - Component props containing children elements
 * @returns {ReactNode} The children elements without any additional wrapper
 */
export const UrlParamsToStoreProvider = ({ children }: PropsWithChildren) => {
  // Extract URL parameters - idOrSlug represents workspace identifier, eventId represents event identifier
  const { idOrSlug, eventId } = useParams<Record<ApiParametersType, string | undefined>>();
  const dispatch = useAppDispatch();

  // Synchronize URL parameters with the Redux store whenever the route changes
  useEffect(() => {
    // Update current workspace ID or slug in store - handles both workspace ID and slug formats
    dispatch(setCurrentWorkspaceIdOrSlug((idOrSlug ?? null) as string));
  }, [dispatch, idOrSlug]);
  useEffect(() => {
    // Update the current event ID in store - null when no event is selected
    dispatch(setCurrentEventId(Number(eventId ?? null) as number));
  }, [dispatch, eventId]);

  // This provider doesn't render any additional markup - it only manages state synchronization
  return children;
};
